import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { GraduationCap, Layout, Terminal, MapPin } from "lucide-react";

export default function About() {
  const { t } = useTranslation();

  const highlights = [
    {
      icon: GraduationCap,
      label: t("about.education"),
      value: t("about.educationVal"),
      detail: "Royal University of Phnom Penh (2021 – 2025)",
    },
    {
      icon: Layout,
      label: t("about.focus"),
      value: t("about.focusVal"),
      detail: "Component architecture, accessibility & responsive design",
    },
    {
      icon: Terminal,
      label: t("about.stack"),
      value: t("about.stackVal"),
      detail: "Clean code with modern toolchains and REST APIs",
    },
    {
      icon: MapPin,
      label: t("about.location"),
      value: t("about.locationVal"),
      detail: "Open to local & remote junior developer opportunities",
    },
  ];

  return (
    <section
      id="about"
      className="py-20 px-6 sm:px-12 lg:px-20 bg-neutral-50/60 dark:bg-black text-neutral-900 dark:text-white border-t border-neutral-200/60 dark:border-neutral-900 relative"
    >
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <span className="inline-block py-1 px-3 rounded-full bg-neutral-200/80 dark:bg-neutral-900 text-neutral-800 dark:text-neutral-300 text-xs font-semibold tracking-wider uppercase mb-3 border border-neutral-300/80 dark:border-neutral-800">
            {t("about.badge")}
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-neutral-900 dark:text-white max-w-2xl leading-tight">
            {t("about.title")}
          </h2>
        </motion.div>

        {/* Narrative Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-6 space-y-4 text-neutral-600 dark:text-neutral-400 text-base sm:text-lg leading-relaxed"
          >
            <p>{t("about.p1")}</p>
            <p>{t("about.p2")}</p>
          </motion.div>

          {/* Quick Highlight Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            {highlights.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className="p-5 rounded-2xl bg-white dark:bg-neutral-950/70 border border-neutral-200/80 dark:border-neutral-800/80 shadow-sm hover:border-neutral-300 dark:hover:border-neutral-700 transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center text-neutral-800 dark:text-neutral-200 mb-3.5 border border-neutral-200/60 dark:border-neutral-800">
                    <Icon size={18} className="text-blue-500" />
                  </div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">
                    {item.label}
                  </div>
                  <div className="text-sm font-bold text-neutral-900 dark:text-white leading-snug mb-1">
                    {item.value}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400 leading-normal">
                    {item.detail}
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

